import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, Download, FileText, ChevronUp, ChevronDown } from 'lucide-react';
import api from '../../api/client';
import { format } from 'date-fns';

function todayIST() {
  return new Date().toLocaleString('sv-SE', { timeZone: 'Asia/Kolkata' }).slice(0, 10);
}

export function fmtDate(iso) {
  if (!iso) return '—';
  try { return format(new Date(iso), 'dd MMM yyyy'); } catch { return '—'; }
}

export function fmtDateTime(iso) {
  if (!iso) return '—';
  try { return format(new Date(iso), 'dd MMM yyyy, HH:mm'); } catch { return '—'; }
}

export function fmtDuration(minutes) {
  if (minutes == null || minutes < 0) return '—';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}m`;
  return `${h}h ${m}m`;
}

const STATUS_COLORS = {
  pending: 'bg-yellow-100 text-yellow-700',
  approved: 'bg-blue-100 text-blue-700',
  out: 'bg-orange-100 text-orange-700',
  returned: 'bg-green-100 text-green-700',
  completed: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
  used: 'bg-purple-100 text-purple-700',
  expired: 'bg-zinc-200 text-zinc-600',
  cancelled: 'bg-zinc-200 text-zinc-500',
  active: 'bg-emerald-100 text-emerald-700',
  checked_in: 'bg-blue-100 text-blue-700',
  checked_out: 'bg-zinc-200 text-zinc-600',
  expected: 'bg-yellow-100 text-yellow-700',
  official: 'bg-blue-100 text-blue-700',
  personal: 'bg-purple-100 text-purple-700',
  inside: 'bg-emerald-100 text-emerald-700',
  outside: 'bg-zinc-200 text-zinc-600',
  returnable: 'bg-blue-100 text-blue-700',
  non_returnable: 'bg-zinc-200 text-zinc-600',
  business: 'bg-blue-100 text-blue-700',
  vendor: 'bg-purple-100 text-purple-700',
  candidate: 'bg-orange-100 text-primary',
  regular: 'bg-zinc-200 text-zinc-700',
  other: 'bg-yellow-100 text-yellow-700',
};

export function StatusChip({ value }) {
  if (!value) return <span className="text-zinc-400">—</span>;
  const cls = STATUS_COLORS[value] || 'bg-zinc-100 text-zinc-600';
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold capitalize ${cls}`}>
      {value.replace(/_/g, ' ')}
    </span>
  );
}

export function BoolChip({ value }) {
  return value
    ? <span className="inline-block px-2 py-0.5 rounded text-xs font-semibold bg-red-100 text-red-700">Yes</span>
    : <span className="text-zinc-400">No</span>;
}

function buildInitParams(filters, defaultSort, defaultOrder) {
  const base = { from: todayIST(), to: todayIST(), date: todayIST(), page: 1, limit: 50, sortBy: defaultSort, order: defaultOrder };
  filters.forEach(f => { if (f.defaultValue !== undefined) base[f.name] = f.defaultValue; });
  return base;
}

export default function ReportLayout({
  title,
  description,
  endpoint,
  columns,
  filters = [],
  defaultSort = 'created_at',
  defaultOrder = 'desc',
  singleDate = false,
  exportFilename,
}) {
  const today = todayIST();
  const [params, setParams] = useState(() => buildInitParams(filters, defaultSort, defaultOrder));
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(null);
  const [usersMap, setUsersMap] = useState({});   // keyed by role string ('' = all non-guard)
  const [departments, setDepartments] = useState([]);

  useEffect(() => {
    const selects = filters.filter(f => f.type === 'userSelect');
    selects.forEach(f => {
      const key = f.role || '';
      const params = f.role ? { role: f.role } : {};
      api.get('/users', { params })
        .then(({ data: u }) => {
          const list = f.role ? u : u.filter(x => x.role !== 'guard');
          setUsersMap(prev => ({ ...prev, [key]: list }));
        })
        .catch(() => {});
    });
    if (filters.some(f => f.type === 'departmentSelect')) {
      api.get('/users')
        .then(({ data: u }) => {
          const depts = [...new Set(u.map(x => x.department).filter(Boolean))].sort();
          setDepartments(depts);
        })
        .catch(() => {});
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    api.get(endpoint, { params })
      .then(({ data: res }) => {
        if (!active) return;
        setData(res.data || []);
        setTotal(res.total || 0);
      })
      .catch(e => {
        if (!active) return;
        setError(e?.response?.data?.message || 'Failed to load report');
        setData([]);
        setTotal(0);
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [params]); // eslint-disable-line react-hooks/exhaustive-deps

  const setParam = (key, value) => setParams(prev => ({ ...prev, [key]: value, page: 1 }));
  const clearAll = () => setParams(buildInitParams(filters, defaultSort, defaultOrder));

  const sort = (col) => {
    setParams(prev => ({
      ...prev,
      sortBy: col,
      order: prev.sortBy === col ? (prev.order === 'asc' ? 'desc' : 'asc') : 'desc',
      page: 1,
    }));
  };

  const exportFile = async (type) => {
    setExporting(type);
    try {
      const { page: _p, limit: _l, ...rest } = params;
      const res = await api.get(endpoint, { params: { ...rest, export: type }, responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      const name = exportFilename || title.toLowerCase().replace(/\s+/g, '-');
      a.download = `${name}-${format(new Date(), 'yyyyMMdd')}.${type}`;
      // Anchor must be in the DOM for programmatic click to trigger a download
      // in Firefox/Safari; delay revocation so the browser can start the download.
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 250);
    } catch (e) {
      const msg = e?.response?.data instanceof Blob
        ? 'Export failed — server returned an error.'
        : (e?.response?.data?.message || 'Export failed. Please try again.');
      setError(msg);
    } finally {
      setExporting(null);
    }
  };

  const totalPages = Math.ceil(total / params.limit) || 1;
  const fromIdx = (params.page - 1) * params.limit + 1;
  const toIdx = Math.min(params.page * params.limit, total);

  return (
    <div className="space-y-4 fade-up">
      <div>
        <Link to="/reports" className="inline-flex items-center gap-1 text-xs text-zinc-400 hover:text-primary mb-3">
          <ChevronLeft size={12} /> Reports
        </Link>
        <div className="overline">Admin Report</div>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="heading text-2xl font-black text-ink">{title}</h1>
            <p className="text-sm text-zinc-500 mt-1">{description}</p>
          </div>
          <div className="flex gap-2">
            <button onClick={() => exportFile('csv')} disabled={!!exporting}
              className="btn-outline text-sm flex items-center gap-1.5 py-1.5 px-3">
              <Download size={13} />
              {exporting === 'csv' ? 'Exporting…' : 'CSV'}
            </button>
            <button onClick={() => exportFile('pdf')} disabled={!!exporting}
              className="btn-outline text-sm flex items-center gap-1.5 py-1.5 px-3">
              <FileText size={13} />
              {exporting === 'pdf' ? 'Exporting…' : 'PDF'}
            </button>
          </div>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="card p-4">
        <div className="flex flex-wrap gap-3 items-end">
          {singleDate ? (
            <div>
              <label className="overline block mb-1">Date</label>
              <input type="date" value={params.date} max={today}
                onChange={e => setParam('date', e.target.value)}
                className="input text-sm h-9" />
            </div>
          ) : (
            <>
              <div>
                <label className="overline block mb-1">From</label>
                <input type="date" value={params.from} max={params.to || today}
                  onChange={e => setParam('from', e.target.value)}
                  className="input text-sm h-9" />
              </div>
              <div>
                <label className="overline block mb-1">To</label>
                <input type="date" value={params.to} min={params.from} max={today}
                  onChange={e => setParam('to', e.target.value)}
                  className="input text-sm h-9" />
              </div>
            </>
          )}

          {filters.map(f => {
            if (f.type === 'select') return (
              <div key={f.name}>
                <label className="overline block mb-1">{f.label}</label>
                <select value={params[f.name] ?? f.defaultValue ?? 'all'}
                  onChange={e => setParam(f.name, e.target.value)}
                  className="input text-sm h-9">
                  <option value="all">All</option>
                  {f.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            );
            if (f.type === 'search') return (
              <div key={f.name}>
                <label className="overline block mb-1">{f.label}</label>
                <input type="text" value={params[f.name] || ''} placeholder={f.placeholder || ''}
                  onChange={e => setParam(f.name, e.target.value)}
                  className="input text-sm h-9" style={{ minWidth: 140 }} />
              </div>
            );
            if (f.type === 'userSelect') {
              const list = usersMap[f.role || ''] || [];
              return (
                <div key={f.name}>
                  <label className="overline block mb-1">{f.label}</label>
                  <select value={params[f.name] || ''}
                    onChange={e => setParam(f.name, e.target.value)}
                    className="input text-sm h-9">
                    <option value="">All</option>
                    {list.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                </div>
              );
            }
            if (f.type === 'departmentSelect') return (
              <div key={f.name}>
                <label className="overline block mb-1">{f.label}</label>
                <select value={params[f.name] || ''}
                  onChange={e => setParam(f.name, e.target.value)}
                  className="input text-sm h-9">
                  <option value="">All</option>
                  {departments.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
            );
            return null;
          })}

          <button onClick={clearAll} className="btn-ghost text-sm h-9 self-end">Clear</button>

          <div className="ml-auto">
            <label className="overline block mb-1">Per page</label>
            <select value={params.limit} onChange={e => setParam('limit', parseInt(e.target.value, 10))}
              className="input text-sm h-9">
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>
      </div>

      {error && (
        <div className="card p-4 text-sm text-red-700 bg-red-50 border border-red-200">{error}</div>
      )}

      {/* Table */}
      <div className="card overflow-hidden">
        <div className="px-4 py-3 border-b border-zinc-100 flex items-center justify-between">
          <span className="text-sm text-zinc-500">
            {loading ? 'Loading…' : total === 0 ? 'No records' : `Showing ${fromIdx}–${toIdx} of ${total}`}
          </span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-zinc-50 border-b border-zinc-200">
                {columns.map(col => (
                  <th key={col.key}
                    className={`px-4 py-2.5 text-left text-xs font-bold text-zinc-500 uppercase tracking-wider whitespace-nowrap${col.sortable !== false ? ' cursor-pointer select-none hover:text-ink' : ''}`}
                    onClick={() => col.sortable !== false && sort(col.key)}>
                    <span className="inline-flex items-center gap-1">
                      {col.label}
                      {col.sortable !== false && params.sortBy === col.key && (
                        params.order === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />
                      )}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {loading ? (
                <tr><td colSpan={columns.length} className="px-4 py-12 text-center text-zinc-400">Loading…</td></tr>
              ) : data.length === 0 ? (
                <tr><td colSpan={columns.length} className="px-4 py-12 text-center text-zinc-400">No records match the selected filters</td></tr>
              ) : data.map((row, i) => (
                <tr key={row.id || i} className="hover:bg-zinc-50">
                  {columns.map(col => (
                    <td key={col.key} className="px-4 py-3 text-zinc-700 whitespace-nowrap max-w-xs truncate">
                      {col.render ? col.render(row[col.key], row) : (row[col.key] ?? '—')}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="px-4 py-3 border-t border-zinc-100 flex items-center justify-between">
            <button disabled={params.page <= 1}
              onClick={() => setParams(p => ({ ...p, page: p.page - 1 }))}
              className="btn-outline text-sm py-1 px-3 disabled:opacity-40">
              ← Prev
            </button>
            <span className="text-sm text-zinc-500">Page {params.page} of {totalPages}</span>
            <button disabled={params.page >= totalPages}
              onClick={() => setParams(p => ({ ...p, page: p.page + 1 }))}
              className="btn-outline text-sm py-1 px-3 disabled:opacity-40">
              Next →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
